import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import bytes from 'bytes';
import intl from 'react-intl-universal';
// import Drift from 'react-driftjs';

import { ImageresizeApi, trackPageView, Raven } from 'modules';
import { BannerAd, Box, BulkList, Button, ImagePicker, ShareBox } from 'components';
import { GENERIC_UPLOAD_ERROR, MAX_UPLOAD_COUNT } from 'constants/images';
import { getId, getUploadedImageErrorMessage, throttle, validateProcessResponse } from 'utils';
// getUrlImageType
import TwitterIcon from 'assets/icons/twitter.svg';
import styles from './styles';

function sumProperty(prop, data) {
  return data.map((el) => el[prop]).reduce((sum, item) => sum + item, 0);
}

export default class PdfCompressor extends Component {

  constructor(props) {
    super(props);

    this.state = {
      step: 'select',
      loaded: false,
      bulkImages: [],
      maxLimitReached: false,
      urlValues: {}
    };
  }

  handleDropImages = (acceptedFiles, rejectedFiles) => {

    // The upload failed, show an error
    if (rejectedFiles.length) {
      return this.setState({
        step: 'error',
        error: rejectedFiles.map(getUploadedImageErrorMessage)[0]
      });
    }

    const { urlValues } = this.state;

    const urlCount = Object.values(urlValues).filter((val) => !!val).length;

    let currentFiles = this.state.bulkImages.length + urlCount;
    if (currentFiles >= MAX_UPLOAD_COUNT) {
      return;
    }
    let limitReached = this.state.maxLimitReached;
    if (acceptedFiles.length > MAX_UPLOAD_COUNT - currentFiles) {
      limitReached = true;
      acceptedFiles = acceptedFiles.slice(0, MAX_UPLOAD_COUNT - currentFiles);
    }
    // Format the files in a way that's useful for the rest of the app and API
    const images = acceptedFiles.map((file) => ({
      id: getId(),
      contentLength: file.size,
      name: file.name,
      state: 'ready',
      uploadType: 'image',
      uploadProgress: -1,
      value: file
    }));

    // Store the successful uploads
    this.setState(({ bulkImages }) => ({
      maxLimitReached: limitReached,
      bulkImages: [...bulkImages, ...images]
    }));
  };

  handleChangeUrl = (name, value) => this.setState(({ urlValues }) => ({
    urlValues: {
      ...urlValues,
      [name]: value
    }
  }));

  removeImage = (id) => this.setState(({ bulkImages }) => ({
    bulkImages: bulkImages.filter((image) => image.id !== id)
  }));

  uploadImages = () => new Promise((resolve, reject) => {
    const { urlValues } = this.state;
    const urls = Object.values(urlValues).map((value) => ({
      id: getId(),
      name: value,
      state: 'ready',
      uploadType: 'url',
      uploadProgress: -1,
      value
    }));
    const bulkImages = [...this.state.bulkImages, ...urls];
    let inFlightUploads = 0;

    // Set up the final list including urls and uploaded images, also cleanup urls
    this.setState({
      urlValues: {},
      bulkImages
    });

    // When a compress call is successful, call resolve as soon as we can
    const compressSuccessful = () => {
      inFlightUploads -= 1;

      if (inFlightUploads === 0) {
        resolve(this.state.bulkImages);
      }
    };

    // When an upload is successful, compress it
    const uploadSuccessful = (image) => {
      ImageresizeApi.editImages([{ ...image, compress: { lossless: true } }])
        .then(validateProcessResponse)
        .then(({ results }) => {
          this.setState(
            ({ bulkImages }) => ({
              bulkImages: bulkImages.map((bulkImage) => bulkImage.id === image.id ? {
                ...image,
                state: 'archiving',
                newImageId: results[0].imageId,
                newContentLength: results[0].contentLength
              } : bulkImage)
            }),
            () => {
              compressSuccessful();
            }
          );
        })
        .catch((err) => reject(err));
    };

    // As each upload finishes, update the app state. Once all uploads are complete, tell our parents.
    const uploadDone = (err, data) => {

      if (err) {
        return reject(err);
      }

      const { image, result } = data;

      // The image has already been uploaded
      if (image.imageId) {
        return uploadSuccessful(image);
      }

      this.setState(
        ({ bulkImages }) => {
          const newImage = {
            ...image,
            uploadProgress: 1,
            contentLength: result.contentLength,
            dimensions: result.dimensions,
            imageId: result.imageId,
            preview: ImageresizeApi.getImageUrl(result.imageId),
            state: 'compressing',
            type: 'pdf',
            name: image.uploadType === 'image' ?
              image.value.name :
              image.value
          };

          return {
            bulkImages: bulkImages.map((item) => (item.id === newImage.id) ? newImage : item)
          };
        },
        () => {
          uploadSuccessful(this.state.bulkImages.find((bulkImage) => bulkImage.id === image.id));
        }
      );
    };

    // Update the progress of each image that's being uploaded, batch state updates so the UI doesn't get overloaded
    const updateProgress = ((self) => {
      const status = {};
      const updateState = throttle(() => self.setState(({ bulkImages }) => ({
        bulkImages: bulkImages.map((item) => {

          // Only update progress if this value is larger to avoid race conditions
          if (status[item.id] && status[item.id] > item.uploadProgress) {
            return {
              ...item,
              state: 'uploading',
              uploadProgress: status[item.id]
            };
          }

          return item;
        })
      })), 50);

      return (imageId, percentComplete) => {
        status[imageId] = percentComplete;

        updateState();
      };
    })(this);

    // Trigger the upload
    bulkImages.forEach((image) => {
      inFlightUploads += 1;

      // If the image is somehow already uploaded, don't reupload it.
      if (image.imageId) {
        return uploadDone(null, { image });
      }

      const upload = ImageresizeApi.postImages([image]);

      upload.on('error', (ev) => uploadDone(new Error(GENERIC_UPLOAD_ERROR)));
      upload.on('progress', ({ percentComplete }) => updateProgress(image.id, percentComplete));
      upload.on('load', () => {

        if (
          upload.xhr.status !== 200 ||
          !upload.response.results ||
          !upload.response.results.length ||
          upload.response.results.find((result) => result.state === 'error')
        ) {
          return uploadDone(new Error(GENERIC_UPLOAD_ERROR));
        }

        const result = upload.response.results[0];

        uploadDone(null, { result, image });
      });
    });
  });

  handleCompressImages = () => {
    trackPageView('/compress-results.html');

    this.uploadImages()
      .then((bulkImages) => {
        const convertedImages = bulkImages.map((bulkImage, idx) => ({
          originalImage: bulkImage,
          imageId: bulkImage.newImageId
        }));

        return ImageresizeApi.createArchive(convertedImages, 'Images');
      })
      .then(validateProcessResponse)
      .then(({ results }) => this.setState(({ bulkImages }) => ({
        result: results[0],
        bulkImages: bulkImages.map((bulkImage) => ({ ...bulkImage, state: 'done' }))
      })))
      .catch((error) => this.setState({
        step: 'error',
        error
      }));
  };

  handleUploadErrors = (errors) => {
    this.setState({
      step: 'error',
      error: new Error(errors[0])
    })

    Raven.clog(errors[0], "CompressImage");
  };
  isUploadInProgress = () => {
    const { bulkImages } = this.state;

    return bulkImages.find((image) => image.state !== 'ready');
  };

  shouldPickerBeDisabled() {
    const { bulkImages, urlValues } = this.state;
    const urlCount = Object.values(urlValues).filter((val) => !!val).length;

    return !!(this.isUploadInProgress() || (urlCount + bulkImages.length) >= MAX_UPLOAD_COUNT);
  }

  shouldButtonBeActive() {
    const { bulkImages, result, urlValues } = this.state;
    const urlsPopulated = Object.values(urlValues).find((val) => !!val);
    const uploadsInProgress = bulkImages.find((image) => image.state !== 'ready');

    return !!((bulkImages.length || urlsPopulated) && !uploadsInProgress && !result);
  }

  renderList() {
    const { bulkImages } = this.state;

    if (!bulkImages.length) {
      return null;
    }

    return (
      <BulkList
        images={bulkImages}
        removeImage={this.removeImage}
        type="compress"
      />
    );
  }

  renderButton() {

    if (this.isUploadInProgress()) {
      return null;
    }

    return (
      <div className="selection-box">
        <Button
          className="main-button"
          disabled={!this.shouldButtonBeActive()}
          onClick={this.handleCompressImages}
        >
          {intl.get('Compress PDF')}
        </Button>
      </div>
    );
  }

  renderTotalSavings(location) {
    const { bulkImages } = this.state;
    const originalSize = sumProperty('contentLength', bulkImages);
    const newSize = sumProperty('newContentLength', bulkImages);
    const savingsInBytes = bytes(originalSize - newSize);
    const percentChange = Math.round((1 - (newSize / originalSize)) * 100);

    // Let people know how much disk space we just saved
    if (location === 'tweet') {
      return `I just saved ${savingsInBytes} by compressing my PDFs on https://imageresize.org`;
    }

    // Somehow the result is larger, tell the user
    if (newSize > originalSize) {
      return `We couldn't compress these images (${bytes(newSize - originalSize)} increase).`;
    }

    // If the image was compressed already, we can't do much more
    if (percentChange < 1) {
      return `You saved less than 1% (${savingsInBytes} total).`;
    }

    // We should almost always be sending this successful message
    return `You just saved ${percentChange}% (${savingsInBytes} total)!`;
  }

  renderDownload() {
    const { result } = this.state;

    if (!result) {
      return null;
    }

    return (
      <div className="selection-box">
        <Box>
          <p className={styles.totalSavings}>
            {this.renderTotalSavings()}
          </p>
          <div className={styles.buttons}>
            <a
              className={styles.tweetButton}
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(this.renderTotalSavings('tweet'))}`}
              target="_blank"
            >
              <TwitterIcon className={styles.twitterIcon} height="24" width="24" />
              {intl.get('Tweet Your Savings')}
            </a>
            <a
              className={styles.downloadButton}
              href={ImageresizeApi.getArchiveUrl(result.archiveId, `images-${new Date().toISOString()}`)}
              target="_blank"
            >
              {intl.get('Download All')}
            </a>
          </div>
        </Box>
      </div>
    );
  }
  renderShareBox() {
    const { result } = this.state;

    if (!result) {
      return null;
    }

    return (
      <div className="selection-box">
        <ShareBox />
      </div>
    );
  }

  renderError() {
    const { error } = this.state;

    return (
      <div className="error">
        <p>'{intl.get('The pdf could not be resized:')}</p>
        <p className="errorMessage">{error.message}</p>
      </div>
    );
  }
  componentDidMount() {
    this.setState({ loaded: true });
  }
  renderDrift() {
    // if(this.state.loaded) {
    //   return (
    //     <Drift appId="rzyr4ptivr3s" />
    //   )
    // }
  }
  clearLimitReachMsg = () => this.setState({ maxLimitReached: false });

  renderStep() {
    const { step, maxLimitReached } = this.state;

    switch (step) {
      case 'select':
        return (
          <div className="container">
            <div className="center section">
              <h1>{intl.get('PdfCompressor.PageHeader')}</h1>
              <p>{intl.get('PdfCompressor.PageHeaderDescription')}</p>
            </div>
            <div className="selection-box">
              <ImagePicker
                accept=".pdf"
                fileType="PDF"
                buttonText="Choose PDFs to Compress"
                disabled={this.shouldPickerBeDisabled()}
                uploading={this.isUploadInProgress()}
                dropImages={this.handleDropImages}
                changeUrl={this.handleChangeUrl}
                submitUrlUpload={this.handleCompressImages}
                multiple={true}
                maxLimitReached={maxLimitReached}
                clearLimitReachMsg={this.clearLimitReachMsg}
              />
            </div>
            {this.renderList()}
            {this.renderButton()}
            {this.renderDownload()}
            {this.renderShareBox()}
            <BannerAd />
            <div className="content">
              <div className="section">
                <h2>{intl.get('PdfCompressor.HowReducePDF')}</h2>
                <p>{intl.get('PdfCompressor.AnsDesc')}</p>
                <ul className="plain-ul">
                  <li>{intl.get('PdfCompressor.Ans1')}</li>
                  <li>{intl.get('PdfCompressor.Ans2')}</li>
                  <li>{intl.get('PdfCompressor.Ans3')}</li>
                  <li>{intl.get('PdfCompressor.Ans4')}</li>
                  <li>{intl.get('PdfCompressor.Ans5')}</li>
                </ul>
              </div>
              <div className="twoSections">
                <div className="section">
                  <h2>{intl.get('PdfCompressor.WithoutLosingQuality')}</h2>
                  <p>{intl.get('PdfCompressor.WithoutLosingQualityPara')}</p>
                </div>
                <div className="section">
                  <h2>{intl.get('PdfCompressor.CompressPDFOnline')}</h2>
                  <p>{intl.get('PdfCompressor.CompressPDFOnlinePara')}</p>
                </div>
              </div>
              <div className="section">
                <h2>{intl.get('PdfCompressor.AdvancedPDFCompressor')}</h2>
                <p>{intl.get('PdfCompressor.AdvancedPDFCompressorPara')}</p>
              </div>
            </div>
          </div>
        );
      case 'error':
        return this.renderError();
      default:
        return null;
    }
  }

  render() {
    const shareUrl = ImageresizeApi.BASE_URL + '/pdf-compressor';
    const title = 'PDF Compressor - Easily Reduce PDF Size Online for Free';
    const desc = 'This advanced PDF compressor can reduce PDF size without losing quality. There are no watermarks or file limits and it is 100% free. Fast and easy online tool to compress PDF.';
    return (
      <div>
        <Helmet>
          <title>{title}</title>
          <meta
            name="description"
            content={desc}
          />
          <meta property="og:url" content={shareUrl} />
          <meta property="og:type" content="website" />
          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta property="og:image" content="/social.png" />

        </Helmet>
        <BannerAd />
        {this.renderDrift()}
        {this.renderStep()}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/containers/PdfCompressor/index.jsx